/**
 * Evidence Pro Trial Banner
 *
 * Inline banner for Evidence Pro trial users showing days remaining.
 * Appears above the chat input when streaming starts with Pro features.
 *
 * Trigger: Stream starts (first chunk received) + user on trial + Pro features enabled
 * Reset: Component unmounts (navigation) or user dismisses
 *
 * Figma: https://www.figma.com/design/Xpd4SOC8nuSY8x3khvCCJl/Ask-Heidi?node-id=4994-99782
 */

import { TierProFilled } from '@heidi/design-system/icons';
import { showEvidenceProTrialBannerAtom } from '@/atoms/chatAtoms';
import { EVIDENCE_POWER_USER_BANNER_COUNT_THRESHOLD } from '@/atoms/evidenceTrialQueryCountAtom';
import { usePaywallStates } from '@/atoms/paywallAtoms';
import { useUserActivityActionCount } from '@/hooks/useUserActivityActionCount';
import { useIsEvidencePowerUserEnabled } from '@/hooks/useIsEvidencePowerUserEnabled';
import { Button } from '@heidi/design-system/button';
import { useEvidenceAnalytics } from '@/hooks/useEvidenceAnalytics';
import { TypographyP2, TypographyP2Bold } from '@heidi/design-system/typography';
import { IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED } from '@/components/constants/flags';
import { useEvidenceBillingInfo } from '@/hooks/useEvidenceBillingInfo';
import useReverseTrial from '@/hooks/useReverseTrial';
import { cn } from '@/lib/cn';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { UserActivityAction, SubscriptionTier } from '@heidi/schema/ml-scribe';
import { ButtonAction } from '@heidi/runtime/analytics-vocabulary';
import { isAppleCompliantBuild } from '@/utils/appConfiguration';
import { useAtomValue, useSetAtom } from 'jotai';
import { X } from 'lucide-react';
import { AnimatePresence } from 'motion/react';
import * as m from 'motion/react-m';
import { useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { track } from '@/lib/analytics/track';

interface EvidenceProTrialBannerProps {
  /** Optional custom className */
  className?: string;
  /** Test identifier */
  'data-testid'?: string;
}

export const EvidenceProTrialBanner = ({
  className,
  'data-testid': testId = 'evidence-pro-trial-banner',
}: EvidenceProTrialBannerProps) => {
  const intl = useIntl();
  const show = useAtomValue(showEvidenceProTrialBannerAtom);
  const setShow = useSetAtom(showEvidenceProTrialBannerAtom);
  // Once the user crosses the power-user threshold AND the power-user feature flag is
  // enabled, the Evidence power-user banner takes over this slot. Keep this toast
  // suppressed so we don't double-nag. If the flag is off, the power-user banner never
  // renders, so we must NOT suppress this trial toast — otherwise the user sees nothing.
  const { data: evidenceTrialQueryCount = 0 } = useUserActivityActionCount(
    UserActivityAction.evidence_chat_query
  );
  const isPowerUserEnabled = useIsEvidencePowerUserEnabled();
  const isSupplantedByPowerUserBanner =
    isPowerUserEnabled &&
    evidenceTrialQueryCount >= EVIDENCE_POWER_USER_BANNER_COUNT_THRESHOLD;
  const { trialDaysRemaining } = useEvidenceBillingInfo();
  const { isClinicianTrialUser, isClinicianPracticeTrialUser, clinicianTrialDaysLeft } =
    useReverseTrial();
  const { baseProps } = useEvidenceAnalytics();
  const {
    toggleClinicianUpgradeModal,
    togglePracticeUpgradeModal,
  } = usePaywallStates();
  const isFlowEnabled = IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED();

  // Reset banner on unmount (user navigates away)
  useEffect(() => {
    return () => {
      setShow(false);
    };
  }, [setShow]);

  const handleDismiss = useCallback(() => {
    setShow(false);
  }, [setShow]);

  const handleUpgrade = useCallback(() => {
    const openClinician = isFlowEnabled && isClinicianTrialUser;
    const openPractice = isFlowEnabled && isClinicianPracticeTrialUser;

    const targetPlan = openPractice
      ? SubscriptionTier.CLINICIAN_PRO_PLUS
      : SubscriptionTier.CLINICIAN_PRO;

    track({
      event_name: TrackingEventName.ButtonClicked,
      props: {
        ...baseProps,
        action: ButtonAction.EvidenceProTrialClicked,
        label: openPractice
          ? 'practice_upgrade_clicked'
          : 'clinician_upgrade_clicked',
        feature: 'evidence',
        target_plan: targetPlan,
      },
    });
    if (openPractice) {
      togglePracticeUpgradeModal(true);
    } else {
      toggleClinicianUpgradeModal(true);
    }
  }, [
    baseProps,
    isClinicianPracticeTrialUser,
    isClinicianTrialUser,
    isFlowEnabled,
    toggleClinicianUpgradeModal,
    togglePracticeUpgradeModal,
  ]);

  // Determine which trial type and message to show
  const bannerMessage = useMemo(() => {
    if (isFlowEnabled && isClinicianTrialUser) {
      const days = Math.max(0, clinicianTrialDaysLeft);
      return intl.formatMessage(
        {
          defaultMessage:
            'You’ve got {days, plural, one {# day} other {# days}} left in your free Clinician Trial', id: '72FpYbmMsG',
          description:
            'Trial countdown shown in the Evidence trial banner for Clinician Trial users',
        },
        { days }
      );
    }
    if (isFlowEnabled && isClinicianPracticeTrialUser) {
      const days = Math.max(0, clinicianTrialDaysLeft);
      return intl.formatMessage(
        {
          defaultMessage:
            'You’ve got {days, plural, one {# day} other {# days}} left in your free Practice Trial', id: '6gEVdonGts',
          description:
            'Trial countdown shown in the Evidence trial banner for Practice Trial users',
        },
        { days }
      );
    }
    // Default Evidence Pro trial behavior
    return intl.formatMessage(
      {
        defaultMessage:
          '{days, plural, one {# day} other {# days}} remaining on your Evidence Plus trial', id: 'B3S8/EUpkZ',
        description: 'Trial countdown shown in the Evidence Plus trial banner',
      },
      { days: Math.max(0, trialDaysRemaining) }
    );
  }, [
    isFlowEnabled,
    isClinicianTrialUser,
    isClinicianPracticeTrialUser,
    clinicianTrialDaysLeft,
    trialDaysRemaining,
    intl,
  ]);

  return (
    <AnimatePresence>
      {show && !isSupplantedByPowerUserBanner && (
        <m.div
          data-testid={testId}
          initial={{ opacity: 0, y: 8, height: 0 }}
          animate={{ opacity: 1, y: 0, height: 'auto' }}
          exit={{ opacity: 0, y: 8, height: 0 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className={cn(
            // TODO(@jordancollins-heidi): replace border-[#E5E2FF] with semantic color once sorted in figma
            'mb-3 w-full overflow-hidden rounded-md border border-[#E5E2FF] bg-surface px-5 py-3 shadow-sm',
            className
          )}
        >
          {/* Header row with title and close button */}
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center gap-2">
              <TierProFilled className="size-2.5 shrink-0 text-tier-pro" />
              <TypographyP2Bold className="text-tier-pro-text">
                {bannerMessage}
              </TypographyP2Bold>
            </div>
            <Button
              variant="ghost"
              size="xxs"
              iconOnly
              flush="all"
              onClick={handleDismiss}
              aria-label={intl.formatMessage({
                defaultMessage: 'Close', id: '/ad9dZZ97R',
                description: 'Accessible label for closing the Evidence trial banner',
              })}
              className="shrink-0 text-text-tertiary hover:bg-transparent hover:text-text-secondary"
              data-testid={`${testId}-close`}
            >
              <X className="size-4" />
            </Button>
          </div>

          {/* Description row with upgrade CTA */}
          <div className="mt-2 flex items-start justify-between gap-3 ps-[18px]">
            <TypographyP2 className="text-text-primary">
              <FormattedMessage
                defaultMessage="This answer uses curated clinical guidelines and primary evidence so you can see exactly where recommendations come from." id="MkT9uB+DsW"
                description="Description in the Evidence trial banner explaining the value of source control"
              />
            </TypographyP2>
            {!isAppleCompliantBuild() && (
              <Button
                variant="pro"
                size="xs"
                onClick={handleUpgrade}
                className="shrink-0"
                data-testid={`${testId}-upgrade`}
              >
                <FormattedMessage
                  defaultMessage="Upgrade" id="JR8WOuDF+Y"
                  description="Button label in the Evidence trial banner"
                />
              </Button>
            )}
          </div>
        </m.div>
      )}
    </AnimatePresence>
  );
};

// Keep the old export for backwards compatibility
export const EvidenceProTrialToast = EvidenceProTrialBanner;
